import { AddonSearchButton } from "@vyne-shared/components/vyneInputs/inputAddonWrapper/AddonSearchButton";
import { Form, Menu } from "antd";
import { VyneInputWithAddon } from "@vyne-shared/components/vyneInputs/inputAddonWrapper/VyneInputWithAddon";
import { searchPatientMenu } from "./SearchPatientMenu/SearchPatientMenu";
import { usePatientQuery } from "api/clients/usePatientQuery";
import { useRef, useState } from "react";
import type { ExistingPatientResult } from "api/responses";
import type { InputRef } from "antd/es/input";

interface SearchPatientFieldProps {
    /** Callback to execute when an existing P/RP is selected. */
    readonly onSelectExisting: (selected: ExistingPatientResult) => void;

    /** If set, in Mason-integrated sessions, P/RPs not linked to PMS patients are excluded. */
    readonly excludeUnlinked?: boolean;

    /** Indicates an intent to show the search result dropdown, due to the field being actually or recently focused.
     * This intent will be honored only when enough characters have been entered into the field. */
    readonly active: boolean;

    /** Callback to execute when the field gains focus. */
    readonly onFocus: () => void;
}

/** Field that will search across Patients/Responsible Parties and return results in a menu. */
export const SearchPatientField = ({ onSelectExisting, excludeUnlinked, active, onFocus }: SearchPatientFieldProps) => {
    const { searchFilter, setSearchFilter, isFilterValid, validateStatus, help, menu } = useSearchPatientField(
        onSelectExisting,
        excludeUnlinked,
    );
    const inputRef = useRef<InputRef>(null);

    return (
        <>
            <div className={"vyne-search-patient-wrap" + (isFilterValid ? " vyne-search-filter-valid" : "")}>
                <Form.Item
                    label="Search for Patient/Guarantor"
                    validateStatus={validateStatus}
                    help={help}
                    data-testid="queryFullName"
                    htmlFor="search-patient-full-name"
                >
                    <VyneInputWithAddon
                        id="search-patient-full-name"
                        ref={inputRef}
                        autoComplete="off"
                        type="search"
                        value={searchFilter}
                        allowClear
                        onChange={(e) => setSearchFilter(e.target.value)}
                        onFocus={onFocus}
                        addonAfter={(className) => (
                            <AddonSearchButton
                                className={className}
                                aria-label="Search for patient"
                                onClick={() => {
                                    inputRef.current?.focus();
                                    onFocus();
                                }}
                            />
                        )}
                    />
                </Form.Item>
            </div>
            {isFilterValid && active && <Menu mode="inline" className="vyne-search-patient-menu" {...menu} />}
        </>
    );
};

function useSearchPatientField(
    onSelectExisting: (selected: ExistingPatientResult) => void,
    excludeUnlinked: boolean | undefined,
) {
    const [searchFilter, setSearchFilter] = useState("");
    const { isFilterValid, isFetching, data } = usePatientQuery(searchFilter, excludeUnlinked ?? false, 200);

    // display error only in steady state
    const validateStatus = searchFilter && !isFetching && !isFilterValid ? "error" : undefined;
    const help = validateStatus ? "Searching requires at least 3 characters" : undefined;

    const menu = searchPatientMenu(isFetching, data, onSelectExisting);

    return { searchFilter, setSearchFilter, isFilterValid, validateStatus, help, menu } as const;
}
